import type { HideReason, ImportReviewInput, Review, ReviewPlatform } from '../../api/client'

export const PLATFORMS: { value: ReviewPlatform; label: string }[] = [
  { value: 'fresha', label: 'Fresha' },
  { value: 'google', label: 'Google' },
  { value: 'treatwell', label: 'Treatwell' },
  { value: 'booksy', label: 'Booksy' },
  { value: 'other', label: 'Somewhere else' },
]

// The only reasons a review may be hidden (spec 2026-10-08, ADR-023).
export const HIDE_REASONS: { value: HideReason; label: string }[] = [
  { value: 'personal_info', label: "It has someone’s personal or health details" },
  { value: 'abusive', label: "It’s abusive" },
  { value: 'off_topic', label: "It isn’t about my treatments" },
]

export const emptyImport: ImportReviewInput = {
  source_platform: 'fresha', source_platform_label: null, display_name: '', rating: null,
  body: null, reviewed_on: '', service_name: null, genuine: false,
}

// importErrors mirrors catalog.ValidateImportReview so the sheet can say
// what is missing before saving; the server still decides.
export function importErrors(f: ImportReviewInput, today: string): Partial<Record<keyof ImportReviewInput, string>> {
  const e: Partial<Record<keyof ImportReviewInput, string>> = {}
  if (f.source_platform === 'other' && !f.source_platform_label?.trim()) e.source_platform_label = 'Say where the review came from.'
  if (!f.display_name.trim()) e.display_name = 'Enter the name shown on the review.'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.reviewed_on) || f.reviewed_on > today) e.reviewed_on = 'Enter the date of the original review.'
  if (!f.genuine) e.genuine = 'Confirm this is a genuine review you copied.'
  return e
}

// platformLabel is where an imported review came from, in words.
export function platformLabel(platform: ReviewPlatform | null, label: string | null): string {
  if (platform === 'other') return label?.trim() || 'somewhere else'
  return PLATFORMS.find((p) => p.value === platform)?.label ?? 'somewhere else'
}

// importFromReview pre-fills the sheet to edit an imported review. The
// genuine tick was given when it was added, so it stays given.
export function importFromReview(r: Review): ImportReviewInput {
  return {
    source_platform: r.source_platform ?? 'other',
    source_platform_label: r.source_platform_label,
    display_name: r.display_name,
    rating: r.rating,
    body: r.body,
    reviewed_on: r.reviewed_on,
    service_name: r.service_name,
    genuine: true,
  }
}

// ratingSummary is the header: the average of visible reviews from clients.
export function ratingSummary(reviews: Review[]): string {
  const rated = reviews.filter((r) => r.source === 'harmovi' && !r.hidden_reason && r.rating)
  if (rated.length === 0) return 'No reviews from clients yet'
  const avg = rated.reduce((n, r) => n + (r.rating ?? 0), 0) / rated.length
  return `★ ${avg.toFixed(1)} · ${rated.length} verified review${rated.length === 1 ? '' : 's'}`
}

// ratingSummaryWords is the same header for a screen reader, which would read
// the star as "black star": "Rated 4.8 out of 5 from 3 verified reviews".
export function ratingSummaryWords(reviews: Review[]): string {
  const rated = reviews.filter((r) => r.source === 'harmovi' && !r.hidden_reason && r.rating)
  if (rated.length === 0) return ratingSummary(reviews)
  const avg = rated.reduce((n, r) => n + (r.rating ?? 0), 0) / rated.length
  return `Rated ${avg.toFixed(1)} out of 5 from ${rated.length} verified review${rated.length === 1 ? '' : 's'}`
}

export function stars(rating: number | null): string {
  return rating ? '★'.repeat(rating) + '☆'.repeat(5 - rating) : ''
}
